import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { clientApi } from "@/context/ClientAuth";

const inputCls = "w-full bg-[#F9F6F1] border border-[#E5E7EB] focus:border-[#041C3E] outline-none px-4 py-3 text-sm transition-colors";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await clientApi.post("/client/reset-password", { token, new_password: pw });
      toast.success("Your password has been reset. Please sign in.");
      navigate("/login");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not reset your password. Please request a new link.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#041C3E] text-white flex flex-col" data-testid="reset-password-page">
      <header className="px-6 h-16 flex items-center justify-between max-w-6xl mx-auto w-full">
        <Link to="/" className="font-head font-black text-lg">The Next Move<span className="text-[#D94833]"> by Rhellex</span></Link>
        <Link to="/login" className="text-sm text-white/60 hover:text-white transition-colors">Back to sign in</Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md bg-white text-[#041C3E] p-8 sm:p-10"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D94833]">Client access</p>
          <h1 className="font-head text-3xl font-black mt-3 leading-tight">Set a new password</h1>

          {!token ? (
            <p data-testid="reset-invalid" className="mt-6 text-sm text-[#D94833]">
              This reset link is missing or invalid. Please request a new one from the sign in page.
            </p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="reset-form">
              {error && <p data-testid="reset-error" className="text-sm text-[#D94833]">{error}</p>}
              <input data-testid="reset-password" type="password" required minLength={8} placeholder="New password (min 8 characters)" className={inputCls}
                value={pw} onChange={(e) => setPw(e.target.value)} />
              <input data-testid="reset-confirm" type="password" required minLength={8} placeholder="Confirm new password" className={inputCls}
                value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              <button data-testid="reset-submit" type="submit" disabled={busy}
                className="w-full bg-[#041C3E] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#D94833] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {busy && <Loader2 size={16} className="animate-spin" />} {busy ? "Saving…" : "Reset password"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
